import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';
import { GlobalToastManager } from './components/GlobalToastManager';
import { SmartAppBanner } from './components/SmartAppBanner';
import { AuthProvider } from './contexts/AuthContext';
import { modalitiesManager } from './managers/modalitiesManager';
import { initializeRemoteConfig, initializeAnalytics } from './lib/firebase';
import { AuthLifecycleSetup } from './config/setupAuthLifecycle';
import { PushLifecycleSetup } from './config/setupPushLifecycle';
import { UserDataBootstrap } from './config/UserDataBootstrap';

// Debug menu is excluded from production builds. The dynamic import behind a
// static `import.meta.env.MODE` guard lets Vite tree-shake the entire module
// out of the production bundle, while keeping it available on local dev and on
// the deployed development environment (build:dev).
const DebugSidebar = import.meta.env.MODE !== 'production'
  ? lazy(() => import('./components/DebugMenu'))
  : null;

// Import pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import DashboardPage from './pages/DashboardPage';
import AppLayoutPage from './pages/app/AppLayoutPage';
import GamesPage from './pages/app/GamesPage';
import GameDetailsPage from './pages/app/GameDetailsPage';
import LocationSettingsPage from './pages/app/LocationSettingsPage';
import { MarketplacePage } from './pages/MarketplacePage';
import { ExplorePage } from './pages/ExplorePage';
import { ProfilePage } from './pages/ProfilePage';
import { ArenaDetailPage } from './pages/ArenaDetailPage';
import { ArenaSearchPage } from './pages/ArenaSearchPage';
import { TrainingCenterSearchPage } from './pages/TrainingCenterSearchPage';
import { TrainingCenterDetailPage } from './pages/TrainingCenterDetailPage';
import { TrainingCenterClassesPage } from './pages/TrainingCenterClassesPage';
import { DayUseSearchPage } from './pages/DayUseSearchPage';
import { SuggestArenaPage } from './pages/SuggestArenaPage';
import { BookingPage } from './pages/BookingPage';
import { ReservationsPage } from './pages/ReservationsPage';
import { ReservationDetailPage } from './pages/ReservationDetailPage';
import { WorkoutsPage } from './pages/WorkoutsPage';
import { WorkoutDetailPage } from './pages/WorkoutDetailPage';
import { MakeupsPage } from './pages/MakeupsPage';
import { SignedTermsPage } from './pages/SignedTermsPage';
import { MakeupAgendaPage } from './pages/MakeupAgendaPage';
import { PlansPage } from './pages/PlansPage';
import { PlanDetailPage } from './pages/PlanDetailPage';
import { MembershipsPage } from './pages/MembershipsPage';
import { MembershipDetailPage } from './pages/MembershipDetailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AuthConnectPage from './pages/AuthConnectPage';
import { TermosPage } from './pages/TermosPage';
import { PrivacidadePage } from './pages/PrivacidadePage';
import { ExcluirContaPage } from './pages/ExcluirContaPage';
import { ArenasTermosPage } from './pages/ArenasTermosPage';
import { ArenasPrivacidadePage } from './pages/ArenasPrivacidadePage';
import { CentrosDeTreinamentoTermosPage } from './pages/CentrosDeTreinamentoTermosPage';
import { CentrosDeTreinamentoPrivacidadePage } from './pages/CentrosDeTreinamentoPrivacidadePage';
import PayChargePage from './pages/PayChargePage';
import AcceptContractTermsPage from './pages/AcceptContractTermsPage';
import PublicTournamentEventPage from './pages/PublicTournamentEventPage';
import { QRCodeArtPage } from './pages/QRCodeArtPage';
import { ExternalRedirectPage } from './pages/ExternalRedirectPage';

function App() {
  // Carrega as modalidades e inicializa Remote Config e Analytics ao inicializar a aplicação
  useEffect(() => {
    modalitiesManager.loadModalities().catch(err => {
      console.error('[APP] Erro ao carregar modalidades na inicialização:', err)
    })

    initializeRemoteConfig().catch(err => {
      console.error('[APP] Erro ao inicializar Remote Config:', err)
    })

    initializeAnalytics().catch(err => {
      console.error('[APP] Erro ao inicializar Analytics:', err)
    })
  }, [])

  return (
    <AuthProvider>
      <AuthLifecycleSetup />
      <PushLifecycleSetup />
      <UserDataBootstrap />
      <div className="antialiased">
        <Router>
        <Routes>
          {/* New Marketplace as main entry point */}
          <Route path="/" element={<MarketplacePage />} />
          <Route path="/explorar" element={<ExplorePage />} />
          <Route path="/arenas/search" element={<ArenaSearchPage />} />
          <Route path="/cts/search" element={<TrainingCenterSearchPage />} />
          <Route path="/ct/:centerId" element={<TrainingCenterDetailPage />} />
          <Route path="/ct/:centerId/turmas" element={<TrainingCenterClassesPage />} />
          <Route path="/rotativos/search" element={<DayUseSearchPage />} />
          <Route path="/arena/:arenaId" element={<ArenaDetailPage />} />
          <Route path="/arena/:venueId/agendamento" element={<BookingPage />} />
          <Route path="/suggest-arena" element={<SuggestArenaPage />} />
          <Route path="/reservations" element={<ReservationsPage />} />
          <Route path="/reservations/:id" element={<ReservationDetailPage />} />
          <Route path="/treinos" element={<WorkoutsPage />} />
          <Route path="/treinos/:id" element={<WorkoutDetailPage />} />
          <Route path="/reposicoes" element={<MakeupsPage />} />
          <Route path="/termos-assinados" element={<SignedTermsPage />} />
          <Route path="/reposicoes/:trainingCenterId" element={<MakeupAgendaPage />} />
          <Route path="/planos" element={<PlansPage />} />
          <Route path="/planos/:id" element={<PlanDetailPage />} />
          <Route path="/mensalistas" element={<MembershipsPage />} />
          <Route path="/mensalistas/:id" element={<MembershipDetailPage />} />
          <Route path="/perfil" element={<ProfilePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Landing pages (public, no auth) — lead capture for Arenas and Training Centers */}
          <Route
            path="/sistema-professores"
            element={<ExternalRedirectPage to="https://ct.meiucaapp.com.br/" />}
          />
          <Route
            path="/sistema-arenas"
            element={<ExternalRedirectPage to="https://arenas.meiucaapp.com.br/" />}
          />

          {/* "Sign in with Meiuca" — popup aberto por outros sistemas
              (CT-web, etc) pra pedir autorização do AppUser e devolver
              linkToken via window.postMessage. Detalhes em AuthConnectPage. */}
          <Route path="/auth/connect" element={<AuthConnectPage />} />

          {/* Public payer-facing page (no auth) — reached from a WhatsApp / email link */}
          <Route path="/pay/:token" element={<PayChargePage />} />

          {/* Public contract-terms acceptance (no auth) — reached from a WhatsApp link */}
          <Route path="/terms/:token" element={<AcceptContractTermsPage />} />

          {/* Public tournament follow-along (no auth) — reached from a shared event link */}
          <Route path="/tournament-events/:token" element={<PublicTournamentEventPage />} />

          {/* Internal tool (no auth) — QR code art generator for arenas */}
          <Route path="/tools/qr-art" element={<QRCodeArtPage />} />

          {/* Legal pages (public, no auth) — BaaS compliance (Resolucao Conjunta 16/2025 BACEN) */}
          <Route path="/termos" element={<TermosPage />} />
          <Route path="/privacidade" element={<PrivacidadePage />} />
          <Route path="/excluir-conta" element={<ExcluirContaPage />} />
          <Route path="/arenas/termos" element={<ArenasTermosPage />} />
          <Route path="/arenas/privacidade" element={<ArenasPrivacidadePage />} />
          <Route path="/centros-de-treinamento/termos" element={<CentrosDeTreinamentoTermosPage />} />
          <Route path="/centros-de-treinamento/privacidade" element={<CentrosDeTreinamentoPrivacidadePage />} />

          {/* Old routes - hidden but accessible */}
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/old-home" element={<HomePage />} />

          {/* App routes */}
          <Route path="/app" element={<AppLayoutPage />}>
            <Route index element={<GamesPage />} />
            <Route path="games" element={<GamesPage />} />
            <Route path="games/:id" element={<GameDetailsPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="settings/location" element={<LocationSettingsPage />} />
          </Route>
        </Routes>

        {DebugSidebar && (
          <Suspense fallback={null}>
            <DebugSidebar />
          </Suspense>
        )}
        <GlobalToastManager />
        <SmartAppBanner />
      </Router>
      </div>
    </AuthProvider>
  );
}

export default App;
